import { apiGet, apiPost, apiPut, apiDelete } from './client';

/**
 * Phase 71 (FTE-01/FTE-02, D-06) — admin-only employment-relationship CRUD
 * client, matching backend/src/api/routes/employmentRelationships.ts.
 *
 * A user may hold 1-2 concurrent employment relationships (each an employee
 * type + its own FTE + validity). There is no DELETE endpoint — "removing" a
 * relationship means closing it (setting `validTo` to today) via `update`,
 * matching the backend's time-valid-row convention (mirrors UserRate).
 */
export interface EmploymentRelationship {
  id: number;
  userId: number;
  employeeTypeId: number | null;
  fte: number;
  validFrom: string | null;
  validTo: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateEmploymentRelationshipInput {
  employeeTypeId: number;
  fte: number;
  validFrom: string;
  validTo?: string | null;
}

export interface UpdateEmploymentRelationshipInput {
  employeeTypeId?: number;
  fte?: number;
  validTo?: string | null;
}

/** 71-08: an employment change effective from a date — 1 leg (single) or 2 (souběh). */
export interface EmploymentChangeInput {
  effectiveFrom: string;
  legs: Array<{ employeeTypeId: number; fte: number }>;
}

export async function getEmploymentRelationships(
  userId: number,
  history = false
): Promise<EmploymentRelationship[]> {
  const res = await apiGet<{ data: EmploymentRelationship[] }>(
    `/admin/users/${userId}/employment-relationships${history ? '?history=true' : ''}`
  );
  return res.data;
}

/** 71-08: record an employment change; returns the resulting active arrangement. */
export async function recordEmploymentChange(
  userId: number,
  input: EmploymentChangeInput
): Promise<EmploymentRelationship[]> {
  const res = await apiPost<{ data: EmploymentRelationship[] }>(
    `/admin/users/${userId}/employment-relationships/change`,
    input
  );
  return res.data;
}

/** 71-08: delete a mistaken employment relationship (× in history). */
export async function deleteEmploymentRelationship(userId: number, id: number): Promise<void> {
  await apiDelete(`/admin/users/${userId}/employment-relationships/${id}`);
}

export async function createEmploymentRelationship(
  userId: number,
  input: CreateEmploymentRelationshipInput
): Promise<EmploymentRelationship> {
  const res = await apiPost<{ data: EmploymentRelationship }>(
    `/admin/users/${userId}/employment-relationships`,
    input
  );
  return res.data;
}

export async function updateEmploymentRelationship(
  userId: number,
  id: number,
  input: UpdateEmploymentRelationshipInput
): Promise<EmploymentRelationship> {
  const res = await apiPut<{ data: EmploymentRelationship }>(
    `/admin/users/${userId}/employment-relationships/${id}`,
    input
  );
  return res.data;
}
